import {inlineError} from '@s-ui/decorators'

import {NotFoundSharedError} from '../../shared/Errors/NotFoundSharedError.js'
import {GetRemoteSeoContentSeoError} from '../Errors/GetRemoteSeoContentSeoError.js'
import {RemoteSeoContentSeoValueObject} from '../ValueObjects/RemoteSeoContentValueObject.js'
import SeoRepository from './SeoRepository.js'

/**
 * @class
 * @implements SeoRepository
 */
export default class HTTPSeoRepository extends SeoRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Config} deps.config
   * @param {Fetcher} deps.httpFetcher
   * @param {Fetcher} deps.fetchOrFail
   * @param {Function} deps.tagcloudSeoValueObjectFactory
   * @param {FromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper} fromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper
   * @param {Function} deps.toQueryString
   */
  constructor({
    config,
    httpFetcher,
    fetchOrFail,
    fromJsonToTagCloudSeoRequestSeoMapper,
    fromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper,
    fromRawsFaqsToFaqsListVOMapper,
    fromStaticFaqsToFaqsSchemaMarkupVOMapper,
    seoMakeModelFaqsError,
    seoMakeModelFaqsNotFoundError,
    toQueryString
  }) {
    super()

    this._config = config
    this._httpFetcher = httpFetcher
    this._fetchOrFail = fetchOrFail
    this._fromJsonToTagCloudSeoRequestSeoMapper = fromJsonToTagCloudSeoRequestSeoMapper
    this._fromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper =
      fromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper
    this._fromRawsFaqsToFaqsListVOMapper = fromRawsFaqsToFaqsListVOMapper
    this._fromStaticFaqsToFaqsSchemaMarkupVOMapper = fromStaticFaqsToFaqsSchemaMarkupVOMapper
    this._seoMakeModelFaqsError = seoMakeModelFaqsError
    this._seoMakeModelFaqsNotFoundError = seoMakeModelFaqsNotFoundError
    this._toQueryString = toQueryString
    this._isMotos = this._config.get('APP_NAME') === this._config.get('APP_NAME_MOTOS')
    this._NOT_FOUND = this._config.get('COMMON').HTTP_STATUS_CODES.NOT_FOUND
    this._GENERIC_ERROR = this._config.get('COMMON').ERRORS.GENERIC
    this._apiUrl = config.get('API_URL')
    this._contentfulApiUrl = config.get('CONTENTFUL_API_URL')
    this._seoContentApiUrl = config.get('SEO_CONTENT_API_URL')
  }

  /**
   * @param {Object} deps
   * @param {String} deps.pageName
   * @return {Array<TagcloudSeoValueObject>}
   */
  @inlineError
  async getTagcloudSeo({seoDataRequest}) {
    const {hasAttributes, pageName, params} = seoDataRequest.toJSON()

    const tagCloudSeoRequest = this._fromJsonToTagCloudSeoRequestSeoMapper.map({
      pageName,
      ...params
    })

    const {endpoint, query} = tagCloudSeoRequest.toJSON()

    const {searchSectionId, sectionId} = query

    return this._httpFetcher.post(`${this._apiUrl}/${endpoint}`, query).then(({data: {tagclouds}}) =>
      this._fromTagCloudJsonResponseToTagCloudSeoValueObjectSeoMapper.map({
        tagclouds,
        sectionId,
        searchSectionId,
        pageName,
        hasAttributes
      })
    )
  }

  @inlineError
  async getMakeModelFaqs(makeModelFaqsRequest) {
    if (this._isMotos) {
      return Promise.reject(this._seoMakeModelFaqsError('Use case not implemented for MOTOS'))
    }

    const {make, model} = makeModelFaqsRequest.toJSON()

    const queryParams = this._toQueryString({
      make,
      model
    })

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._NOT_FOUND]: this._seoMakeModelFaqsNotFoundError,
        [this._GENERIC_ERROR]: this._seoMakeModelFaqsError
      })
      .get(`${this._contentfulApiUrl}/faqs?${queryParams}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromRawsFaqsToFaqsListVOMapper.map(data)
  }

  @inlineError
  async getStaticFaqs(getStaticFaqsRequestVO) {
    const {path} = getStaticFaqsRequestVO.toJSON()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._NOT_FOUND]: NotFoundSharedError.create,
        [this._GENERIC_ERROR]: this._seoMakeModelFaqsError
      })
      .get(`${this._apiUrl}/faq${path}`)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromStaticFaqsToFaqsSchemaMarkupVOMapper.map({...data, path})
  }

  @inlineError
  async getRemoteSeoContent({canonicalUrl, vehicleCount}) {
    if (!canonicalUrl) {
      return Promise.reject(
        GetRemoteSeoContentSeoError.create('[HTTPSeoRepository#getRemoteSeoContent] canonicalUrl is required')
      )
    }

    const queryParams = this._toQueryString({canonicalUrl, vehicleCount})

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [this._NOT_FOUND]: () => GetRemoteSeoContentSeoError.createNotFound(),
        [this._GENERIC_ERROR]: () =>
          GetRemoteSeoContentSeoError.create(
            '[HTTPSeoRepository#getRemoteSeoContent] Error retrieving remote SEO content'
          )
      })
      .get(`${this._seoContentApiUrl}/seo-page-content?${queryParams}`)

    if (error) return Promise.reject(error)

    return RemoteSeoContentSeoValueObject.createFromPrimitives(response.data)
  }
}
